// The theme cycle — the single appearance control, shared by the landing masthead AND
// the app topbar so the washed theme family switches EVERYWHERE (not just the landing).
// Click cycles to the next theme + loops; `[` / `]` cycle prev/next globally. The label
// rolls split-flap (old slides down, new drops in) like a label-maker. Persists to the
// `wt_appearance` COOKIE (the same key the pre-paint + useAppearance read — one source of
// truth) and repaints the favicon in lockstep. Square / solid; nothing floats.
//
// The valid id list lives in @waytide/core (APPEARANCE_THEME_IDS — the appearance
// authority); this module owns only the display metadata (name / feel / swatch).

import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import {
  APPEARANCE_THEME_IDS,
  appearanceCookie,
  parseAppearanceCookie,
} from "@waytide/core";
import { updateFavicon } from "./favicon";

export type ThemeId = (typeof APPEARANCE_THEME_IDS)[number];
type Meta = { name: string; feel: string; paper: string; ink: string };

// Honest per-theme display: the real paper + ink (mirrors the design tokens; inlined
// because the appearance-scoped tokens only resolve when the WHOLE page is that theme).
const META: Record<ThemeId, Meta> = {
  light: { name: "Newsprint", feel: "warm off-white", paper: "oklch(0.94 0.010 90)", ink: "oklch(0.25 0.006 250)" },
  iris: { name: "Iris", feel: "violet on cream", paper: "oklch(0.94 0.010 90)", ink: "oklch(0.33 0.045 305)" },
  basil: { name: "Basil", feel: "green on cream", paper: "oklch(0.94 0.010 90)", ink: "oklch(0.31 0.035 160)" },
  ledger: { name: "Ledger", feel: "mint ledger", paper: "oklch(0.90 0.028 155)", ink: "oklch(0.31 0.035 160)" },
  rhubarb: { name: "Rhubarb", feel: "pink on mint", paper: "oklch(0.90 0.028 155)", ink: "oklch(0.31 0.06 8)" },
  fog: { name: "Fog", feel: "coastal grey", paper: "oklch(0.88 0.013 235)", ink: "oklch(0.27 0.012 245)" },
  cyan: { name: "Cyan", feel: "pale cyanotype", paper: "oklch(0.88 0.05 215)", ink: "oklch(0.30 0.04 215)" },
  flamingo: { name: "Flamingo", feel: "pink on cyan", paper: "oklch(0.88 0.05 215)", ink: "oklch(0.31 0.05 350)" },
  driftwood: { name: "Driftwood", feel: "brown on fog", paper: "oklch(0.88 0.013 235)", ink: "oklch(0.32 0.04 50)" },
  goldenrod: { name: "Goldenrod", feel: "yellow carbon", paper: "oklch(0.87 0.05 100)", ink: "oklch(0.33 0.035 95)" },
  pollen: { name: "Pollen", feel: "violet on gold", paper: "oklch(0.87 0.05 100)", ink: "oklch(0.33 0.045 305)" },
  mimeo: { name: "Mimeo", feel: "ditto lavender", paper: "oklch(0.85 0.045 305)", ink: "oklch(0.33 0.045 305)" },
  bubblegum: { name: "Bubblegum", feel: "bright pink", paper: "oklch(0.84 0.08 350)", ink: "oklch(0.31 0.05 350)" },
  melon: { name: "Melon", feel: "green on pink", paper: "oklch(0.84 0.08 350)", ink: "oklch(0.31 0.035 160)" },
  kraft: { name: "Kraft", feel: "manila card", paper: "oklch(0.83 0.030 75)", ink: "oklch(0.30 0.022 60)" },
  chambray: { name: "Chambray", feel: "blue on tan", paper: "oklch(0.83 0.030 75)", ink: "oklch(0.27 0.012 245)" },
  riso: { name: "Riso", feel: "fluoro pink", paper: "oklch(0.82 0.085 5)", ink: "oklch(0.31 0.06 8)" },
  punch: { name: "Punch", feel: "blue on pink", paper: "oklch(0.82 0.085 5)", ink: "oklch(0.27 0.012 245)" },
  sepia: { name: "Sepia", feel: "photo brown", paper: "oklch(0.81 0.045 60)", ink: "oklch(0.32 0.04 50)" },
  pistachio: { name: "Pistachio", feel: "green on tan", paper: "oklch(0.81 0.045 60)", ink: "oklch(0.31 0.035 160)" },
  rust: { name: "Rust", feel: "burnt orange", paper: "oklch(0.45 0.07 50)", ink: "oklch(0.90 0.03 60)" },
  patina: { name: "Patina", feel: "teal on rust", paper: "oklch(0.45 0.07 50)", ink: "oklch(0.89 0.03 200)" },
  slate: { name: "Slate", feel: "overcast sea", paper: "oklch(0.43 0.018 235)", ink: "oklch(0.90 0.010 230)" },
  pondweed: { name: "Pondweed", feel: "green on slate", paper: "oklch(0.43 0.018 235)", ink: "oklch(0.86 0.11 145)" },
  blueprint: { name: "Blueprint", feel: "drafting blue", paper: "oklch(0.34 0.055 240)", ink: "oklch(0.90 0.035 235)" },
  drydock: { name: "Drydock", feel: "warm on blue", paper: "oklch(0.34 0.055 240)", ink: "oklch(0.90 0.028 30)" },
  petrol: { name: "Petrol", feel: "dark teal", paper: "oklch(0.33 0.05 200)", ink: "oklch(0.89 0.03 200)" },
  oxblood: { name: "Oxblood", feel: "dried maroon", paper: "oklch(0.32 0.05 25)", ink: "oklch(0.90 0.028 30)" },
  holly: { name: "Holly", feel: "green on red", paper: "oklch(0.32 0.05 25)", ink: "oklch(0.86 0.11 145)" },
  plum: { name: "Plum", feel: "dark eggplant", paper: "oklch(0.30 0.06 330)", ink: "oklch(0.88 0.03 330)" },
  indigo: { name: "Indigo", feel: "deep indigo", paper: "oklch(0.30 0.06 280)", ink: "oklch(0.89 0.03 280)" },
  nebula: { name: "Nebula", feel: "cyan on plum", paper: "oklch(0.30 0.06 330)", ink: "oklch(0.90 0.035 235)" },
  ember: { name: "Ember", feel: "warm on indigo", paper: "oklch(0.30 0.06 280)", ink: "oklch(0.90 0.028 30)" },
  dark: { name: "Carbon", feel: "photocopied night", paper: "oklch(0.25 0.006 250)", ink: "oklch(0.94 0.010 90)" },
  matrix: { name: "Matrix", feel: "green on black", paper: "oklch(0.25 0.006 250)", ink: "oklch(0.86 0.11 145)" },
  synth: { name: "Synth", feel: "magenta on black", paper: "oklch(0.25 0.006 250)", ink: "oklch(0.88 0.03 330)" },
  phosphor: { name: "Phosphor", feel: "green CRT", paper: "oklch(0.22 0.015 150)", ink: "oklch(0.86 0.11 145)" },
  filament: { name: "Filament", feel: "warm on green", paper: "oklch(0.22 0.015 150)", ink: "oklch(0.90 0.02 80)" },
  char: { name: "Char", feel: "warm near-black", paper: "oklch(0.20 0.008 60)", ink: "oklch(0.90 0.02 80)" },
  pilot: { name: "Pilot", feel: "teal on coal", paper: "oklch(0.20 0.008 60)", ink: "oklch(0.89 0.03 200)" },
};

const THEMES = APPEARANCE_THEME_IDS.map((id) => ({ id, ...META[id] }));
const isThemeId = (v: string | null): v is ThemeId =>
  !!v && (APPEARANCE_THEME_IDS as readonly string[]).includes(v);

/** The saved theme from the cookie (a washed-theme id), or null (system / a11y). */
function savedThemeId(): ThemeId | null {
  if (typeof document === "undefined") return null;
  const v = parseAppearanceCookie(document.cookie);
  return isThemeId(v) ? v : null;
}

export function ThemeCycle() {
  const [theme, setTheme] = useState<ThemeId>("dark");
  const [rollFrom, setRollFrom] = useState<ThemeId | null>(null);
  const liveRef = useRef<HTMLSpanElement | null>(null);
  const rollTimer = useRef<number | undefined>(undefined);

  const cycle = useCallback((dir: 1 | -1) => {
    setTheme((prev) => {
      const ids = APPEARANCE_THEME_IDS;
      const next = ids[(ids.indexOf(prev) + dir + ids.length) % ids.length];
      document.documentElement.setAttribute("data-appearance", next);
      updateFavicon();
      try {
        document.cookie = appearanceCookie(next);
      } catch {
        // No persistence (private mode) — the in-session attribute pin still holds.
      }
      const def = THEMES.find((t) => t.id === next);
      if (liveRef.current)
        liveRef.current.textContent = def ? `${def.name} theme` : next;
      const reduce =
        typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      if (!reduce) {
        setRollFrom(prev);
        window.clearTimeout(rollTimer.current);
        rollTimer.current = window.setTimeout(() => setRollFrom(null), 480);
      }
      return next;
    });
  }, []);

  // Mount: a saved cookie theme wins; otherwise reflect the OS-resolved mode (no pin).
  useEffect(() => {
    const saved = savedThemeId();
    if (saved) {
      document.documentElement.setAttribute("data-appearance", saved);
      updateFavicon();
      setTheme(saved);
      return;
    }
    // matchMedia is absent in jsdom (the test renderer mounts this in the topbar).
    const osLight =
      window.matchMedia?.("(prefers-color-scheme: light)")?.matches ?? false;
    setTheme(osLight ? "light" : "dark");
    updateFavicon();
  }, []);

  // Global `[` / `]` cycling, guarded against typing contexts / IME / modifier combos.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "[" && e.key !== "]") return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.isComposing) return;
      const el = e.target as HTMLElement | null;
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)))
        return;
      e.preventDefault();
      cycle(e.key === "]" ? 1 : -1);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cycle]);

  useEffect(() => () => window.clearTimeout(rollTimer.current), []);

  const cur = THEMES.find((t) => t.id === theme) ?? THEMES[THEMES.length - 1];
  const out = rollFrom != null ? THEMES.find((t) => t.id === rollFrom) : null;
  const rolling = out != null;

  return (
    <div className="wt-themecycle-wrap" data-testid="landing-appearance-toggle">
      <button
        type="button"
        className="wt-themecycle"
        data-testid="landing-theme-cycle"
        aria-label={`Theme: ${cur.name} (${cur.feel}). Click to cycle to the next theme`}
        onClick={() => cycle(1)}
      >
        <span className="wt-themecycle__win">
          {out ? (
            <span
              key={`out-${out.id}`}
              className="wt-themecycle__row wt-themecycle__row--out"
              aria-hidden
              onAnimationEnd={() => setRollFrom(null)}
            >
              <ThemeSwatch id={out.id} />
              <span className="wt-themecycle__name">{out.name}</span>
            </span>
          ) : null}
          <span
            key={`in-${cur.id}`}
            className={`wt-themecycle__row${rolling ? " wt-themecycle__row--in" : ""}`}
          >
            <ThemeSwatch id={cur.id} />
            <span className="wt-themecycle__name">{cur.name}</span>
          </span>
        </span>
        <span className="wt-themecycle__cue" aria-hidden>
          ↻
        </span>
      </button>
      <span
        ref={liveRef}
        className="wt-themecycle__live"
        aria-live="polite"
        aria-atomic="true"
      />
    </div>
  );
}

/** A theme swatch — a square split into the theme's real paper + ink (honest preview). */
function ThemeSwatch({ id }: { id: ThemeId }) {
  const sw = META[id];
  return (
    <span
      className="wt-themecycle__swatch"
      aria-hidden
      style={{ "--rcpt-sw-paper": sw.paper, "--rcpt-sw-ink": sw.ink } as CSSProperties}
    />
  );
}
